/* eslint-disable react-hooks/rules-of-hooks */
import { IntlShape } from 'react-intl';
import { round } from 'mathjs';

import store from 'store/storeConfig';

export const getDefaultValForClass = (className: string, rawUnit: string): string => {
  const unit = normalizeUnitKey(rawUnit);
  const { settings } = store.getState();
  const measurementClasses: IMeasurementClass[] = settings.citc.measurementClasses || [];

  const foundClass = measurementClasses.find((measClass) => measClass.className === className);
  const unitDetails = foundClass?.units?.find((unitDetail) => unitDetail[unit])?.[unit];

  if (!unitDetails) return '0';

  const decimals = unitDetails.decimals;
  const powerOfTen = Math.pow(10, decimals);
  const isHeightInFeet = className === 'height' && unit === 'ft';

  if (unitDetails.defaultValue) {
    if (isHeightInFeet) {
      return Math.round(parseInt(unitDetails.defaultValue.toString(), 10)).toFixed(0);
    } else if (className === 'height_inches') {
      const heightClass = measurementClasses.find((measClass) => measClass.className === 'height');
      const heightUnit = heightClass?.units?.find((unitObj) => unitObj['ft']);
      const fullValue = parseFloat(heightUnit?.['ft']?.defaultValue || '0.00');
      const decimalValue = fullValue % 1;
      return (decimalValue * 12).toFixed(0); // Returns inches as integer
    }
    return (Math.round(parseFloat(unitDetails.defaultValue) * powerOfTen) / powerOfTen).toFixed(
      decimals
    );
  }

  const minVal = getMinValForClass(className, unit);
  return (Math.round(minVal * powerOfTen) / powerOfTen).toFixed(decimals);
};

export const normalizeUnitKey = (unit: string) => {
  const { settings } = store.getState();
  return settings.citc?.unitAliases?.[unit] ?? unit;
};

export const getBoundsForClass = (
  className: string,
  rawUnit: string
): { min: string; max: string } | undefined => {
  const unit = normalizeUnitKey(rawUnit);
  const { settings } = store.getState();
  const measurementClasses: IMeasurementClass[] = settings.citc.measurementClasses || [];
  const unitDetails = measurementClasses
    .find((measClass) => measClass.className === className)
    ?.units?.find((unitObj) => unitObj[unit])?.[unit];
  if (!unitDetails) return undefined;
  return { min: unitDetails.min, max: unitDetails.max };
};

export const getMaxValForClass = (className: string, unit: string) => {
  if (className === 'height_inches') {
    return 11;
  }
  const bounds = getBoundsForClass(className, unit);
  return bounds ? parseFloat(bounds.max) : 0;
};

export const getMinValForClass = (className: string, unit: string) => {
  const bounds = getBoundsForClass(className, unit);
  return bounds ? parseFloat(bounds.min) : 0;
};

export const buildUnitOptions = (className: string): ISelectOption[] => {
  const { intl } = store.getState();
  const units: string[] = getAvailableUnitsForClass(className);

  return units.map((unit: string) => {
    const unitString = unit === '%' || unit === '' ? unit : intl.messages[unit] || unit;
    return { label: unitString, value: unit };
  });
};

export const calculateUnitStep = (className: string, unit: string) =>
  1 / Math.pow(10, getDecimals(className, unit));

export const getDecimals = (className: string, rawUnit: string) => {
  const unit = normalizeUnitKey(rawUnit);
  const { settings } = store.getState();
  const measurementClasses: IMeasurementClass[] = settings.citc.measurementClasses || [];

  if (className === 'pressure') return 0;
  const foundClass = measurementClasses.find((measClass) => measClass.className === className);
  if (!foundClass) return 0;
  const unitDetails = foundClass.units?.find((unitObj) => unitObj[unit]);
  if (unitDetails) {
    return unitDetails[unit].decimals;
  }
  return 0;
};

export const buildClassOptions = () => {
  const { intl, settings } = store.getState();
  const measurementClasses: IMeasurementClass[] = settings?.citc?.measurementClasses || [];
  return measurementClasses
    .filter(
      (measClass: IMeasurementClass) =>
        !measClass.parentMeasurement && !measClass.calculated && measClass.className !== 'sleep'
    )
    .map((measClass: IMeasurementClass) => {
      return {
        label: intl.messages[measClass.className],
        value: measClass.className
      };
    })
    .sort((a, b) => {
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });
};

export const buildObservationsOptions = (observations: any[]) => {
  const { intl } = store.getState();
  const options =
    observations
      ?.filter((e) => e.archived === 0 && !!intl.messages[e.measurementClass])
      ?.map((obs) => ({
        label: intl.messages[obs.measurementClass],
        value: obs.measurementClass || ''
      }))
      ?.sort((a, b) => (a.label < b.label ? -1 : 1)) || [];
  return options;
};

export const buildNonCompositeClassOptions = () => {
  const { intl, settings } = store.getState();
  const measurementClasses: IMeasurementClass[] = settings?.citc?.measurementClasses || [];

  return measurementClasses
    .filter((clazz) => !clazz.composite)
    .map((measClass: IMeasurementClass) => {
      return {
        label: intl.messages[measClass.className],
        value: measClass.className
      };
    })
    .sort((a, b) => {
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });
};

export const getAvailableUnitsForClass = (measurementClassName: string): string[] => {
  const { settings } = store.getState();
  const measurementClasses: IMeasurementClass[] = settings.citc.measurementClasses || [];

  const foundClass = measurementClasses.find(
    (measClass) => measClass.className === measurementClassName
  );

  if (foundClass) {
    return foundClass.units?.map((unitObj) => Object.keys(unitObj)[0]) || [];
  }

  return measurementClassName === 'height_inches' ? ['in'] : [''];
};

/**
 * Gets name of the unit pref group for a given measurement class name
 * @param measClasses the set of measurement classes you wish to parse
 * @param measClassName the name of the class for which you need the pref unit
 * @returns the name of the unit preference group to acces in the store || ''
 */
export const getPreferenceUnitGroupForMeasurementClass = (measClassName: string): string => {
  const { settings } = store.getState();
  const measurementClasses: IMeasurementClass[] = settings?.citc?.measurementClasses || [];
  const measurementClass = measurementClasses.find(
    (measClass) => measClass.className === measClassName
  );
  const unitGroup =
    measurementClass?.className &&
    (measurementClass.className.includes('glucose') ? 'glucose' : measurementClass?.className);
  return unitGroup || '';
};

export const computePrefUnit = (
  className: string,
  unitPreferences: IUnitPreference,
  measurementClasses: IMeasurementClass[]
): string => {
  const measurementClass = measurementClasses.find((mc) => mc.className === className);
  const unitGroup =
    measurementClass?.className &&
    ((measurementClass.className.includes('glucose') ? 'glucose' : measurementClass.className) as
      | keyof IUnitPreference
      | false);

  if (unitGroup && unitPreferences[unitGroup]) {
    return unitPreferences[unitGroup];
  }

  return measurementClass?.units ? Object.keys(measurementClass.units[0])[0] : '';
};

export const getPrefUnitForMeasurementClass = (className: string): string => {
  const { settings } = store.getState();
  const unitPreferences: IUnitPreference = settings?.unitPreferences;
  const measurementClasses: IMeasurementClass[] = settings.citc.measurementClasses || [];
  return computePrefUnit(className, unitPreferences, measurementClasses);
};

export const buildChoicesSelectOptions = (className: string, ignoredLowerBound = false) => {
  const { intl, settings } = store.getState();
  const measurementClass = settings.citc?.measurementClasses?.find(
    (x: any) => x.className === className
  );
  const measurementClassOptions: IMeasurementOptions[] = measurementClass?.options;
  let options = measurementClassOptions ? measurementClassOptions : [];

  if (ignoredLowerBound) {
    options = options.filter((option, index) => index > 0);
  }

  const selectOptions: ISelectOption[] = [];
  options?.forEach((option: IMeasurementOptions) => {
    selectOptions.push({ label: intl.messages[option.title], value: option.value });
  });
  return selectOptions;
};

export const getShortTitleClass = (className: string) => {
  const { settings } = store.getState();
  return settings.citc?.measurementClasses?.find((x: any) => x.className === className)?.shortTitle;
};

export const getLongTitleClass = (className: string) => {
  const { settings } = store.getState();
  return (
    settings.citc?.measurementClasses?.find((x: any) => x.className === className)?.longTitle ||
    className
  );
};

export const formatObservations = (observations: ICarePlanObservationTask[]) => {
  const formattedObservations = [...observations];
  formattedObservations.forEach((obs) => (obs.title = obs.mClass));
  return formattedObservations;
};

export const getFormattedMeasurementRecord = (
  measurement: IMeasurementRecord,
  intl: IntlShape
): string => {
  return (
    getFormattedMeasurementValue(measurement, intl) + getFormattedMeasurementUnit(measurement, intl)
  );
};

export const getFormattedMeasurementUnit = (measurement: IMeasurementRecord, intl: IntlShape) => {
  if (measurement.display_unit === 'ft') {
    return intl.formatMessage({ id: 'inKey' });
  } else {
    return getUnitForMeasurementClass(measurement.class, measurement.display_unit, intl);
  }
};

export const getFormattedMeasurementValue = (measurement: IMeasurementRecord, intl: IntlShape) => {
  switch (measurement.class) {
    case 'pressure':
      return getFormattedPressureMeasurement(measurement, intl);
    default:
      if (measurement.display_unit === 'ft') {
        return getFormattedImperialLengthMeasurement(measurement, intl);
      } else {
        return getFormattedValueForMeasurementClass(measurement.display_value, measurement.class);
      }
  }
};

const getFormattedPressureMeasurement = (measurement: IMeasurementRecord, intl: IntlShape) => {
  return `${getFormattedValueForMeasurementClass(
    measurement.systolic,
    'systolic'
  )} / ${getFormattedValueForMeasurementClass(measurement.diastolic, 'diastolic')} `;
};

const getFormattedImperialLengthMeasurement = (
  measurement: IMeasurementRecord,
  intl: IntlShape
) => {
  let formattedString = `${getFormattedValueForMeasurementFeet(measurement.display_value, true)}`;
  formattedString += ` ${getUnitForMeasurementClass(
    measurement.class,
    measurement.display_unit,
    intl
  )} `;
  formattedString += `${getFormattedValueForMeasurementFeet(measurement.display_value, false)} `;
  return formattedString;
};

export const formatHoursAsHM = (value: number): string => {
  const { intl } = store.getState();
  const totalMinutes = Math.round(Number(value) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return intl.messages.sleepDurationHMKey
    .replace('{hours}', hours.toString())
    .replace('{minutes}', minutes.toString());
};

export const getFormattedValueForMeasurementClass = (value: any, measurementClass: string) => {
  const { settings } = store.getState();
  const measurementClasses: IMeasurementClass[] = settings.citc.measurementClasses || [];
  const measurementOptions = measurementClasses.find(
    (m) => m.className === measurementClass
  )?.options;
  const option = measurementOptions?.find((option) => option.value === value);
  if (option) {
    const { intl } = store.getState();
    return intl.messages[option.title];
  }
  switch (measurementClass) {
    case 'weight':
    case 'waist':
    case 'fat_ratio':
    case 'body_temperature':
    case 'A1C':
    case 'skin_temperature':
      return round(value, 1);
    case 'height':
    case 'forcedExpiratoryVolume1':
    case 'forcedExpiratoryVolume1Percent':
    case 'forcedVitalCapacity':
    case 'fivc':
    case 'bmi':
      return round(value, 2);
    case 'cumulativeSleep':
    case 'totalSleep': {
      const sign = value < 0 ? '-' : '';
      const min = Math.floor(Math.abs(value));
      const sec = Math.floor((Math.abs(value) * 60) % 60);
      return sign + (min < 10 ? '0' : '') + min + ':' + (sec < 10 ? '0' : '') + sec;
    }
    case 'sleep':
      return formatHoursAsHM(value);
    case 'glucose_bedtime':
    case 'glucose_fasting':
    case 'glucose_midnight':
    case 'glucose_post_meal':
    case 'glucose_post_meal_breakfast':
    case 'glucose_post_meal_lunch':
    case 'glucose_post_meal_dinner':
    case 'glucose_pre_meal':
    case 'glucose_pre_meal_breakfast':
    case 'glucose_pre_meal_lunch':
    case 'glucose_pre_meal_dinner':
    case 'glucose_unspecified':
    case 'glucose_min':
    case 'glucose_max':
    case 'glucose_avg':
      return Math.round(value * 100) / 100;
    case 'urine_protein':
    case 'calprotectin':
      return value;
    default:
      return Math.round(value);
  }
};

export const getFormattedValueForMeasurementFeet = (value: any, isft: boolean) => {
  const roundedValue = round(value, 2);
  return isft ? Math.floor(roundedValue) : Math.floor((roundedValue % 1) * 12);
};

export const getUnitForMeasurementClass = (measurementClass: string, unit: string, intl: any) => {
  switch (measurementClass) {
    case 'respiratoryRate':
    case 'respiratory_rate_post_exercise':
    case 'steps': {
      return intl.formatMessage({ id: unit, defaultMessage: unit });
    }
    case 'totalSleep':
    case 'breathsPerMinute':
    case 'cumulativeSleep': {
      return intl.formatMessage({ id: 'hourHoursKey' });
    }
    case 'sleep':
      return '';
    default:
      return unit;
  }
};
